import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, PlusLg, Gift, InfoCircle } from 'react-bootstrap-icons';
import { useLocale } from '../i18n/LocaleContext';
import {
  fetchPrizePool, createPrizePoolOffer, updatePrizePoolOffer, withdrawPrizePoolOffer, fetchBrandProducts,
} from '../api/brands';
import {
  OFFER_TYPES, EMPTY_OFFER, formFromOffer, payloadFromForm, offerProblem, maxDiscountPreview, EDITABLE_WHEN_RESERVED,
} from './prizePool';
import ConfirmDialog from './ConfirmDialog';
import './PrizePoolTab.scss';

const LOCALES = { hu: 'hu-HU', en: 'en-GB' };

function budapestMonth(now = new Date()) {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Budapest', year: 'numeric', month: '2-digit' }).format(now).slice(0, 7);
}

function shiftMonth(month, delta) {
  const [y, m] = month.split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1 + delta, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

const money = (v, cur, lang) => `${Number(v).toLocaleString(LOCALES[lang] || 'en-GB')} ${cur || ''}`.trim();

function magnitude(o, lang) {
  if (o.discount_type === 'percent') return `−${Number(o.discount_value)}%`;
  if (o.discount_type === 'fixed') return `−${money(o.discount_value, o.currency, lang)}`;
  return '';
}

function productListOf(list) {
  return Array.isArray(list) ? list : list?.data ?? list?.products ?? [];
}

function errorText(t, e) {
  if (!e) return null;
  if (e.data?.message === 'offer_reserved' && e.data?.details?.days) return t('prizePool.error.offer_reserved', { days: e.data.details.days.join(', ') });
  const key = `prizePool.error.${e.data?.message}`;
  const field = typeof e.data?.details === 'string' ? t(`prizePool.field.${e.data.details}`) : '';
  const text = t(key, { field });
  return text === key ? t('prizePool.error.generic') : text;
}

function Preview({ form, salePercent, t, lang }) {
  const p = maxDiscountPreview(form, salePercent);
  if (!p) return null;
  return (
    <div className="pp-preview" data-testid="pp-preview">
      <strong>{t('prizePool.preview.title')}</strong>
      {p.kind === 'percent' && (
        <span>
          {p.combinable && p.sale > 0
            ? t('prizePool.preview.percentStacked', { max: p.max, sale: p.sale, coupon: p.coupon })
            : t('prizePool.preview.percent', { max: p.max })}
        </span>
      )}
      {p.kind === 'fixed' && (
        <span>
          {t('prizePool.preview.fixed', { amount: money(p.coupon, p.currency, lang), min: p.minCart ? money(p.minCart, p.currency, lang) : '—', share: p.maxShare ?? '—' })}
        </span>
      )}
      <span className="pp-muted">{p.combinable ? t('prizePool.preview.combinable') : t('prizePool.preview.notCombinable')}</span>
    </div>
  );
}

function ProductPicker({ products, selected, onChange, disabled, t }) {
  const [q, setQ] = useState('');
  const shown = useMemo(() => {
    const term = q.trim().toLowerCase();
    return products.filter((p) => !term || (p.name || p.title || '').toLowerCase().includes(term)).slice(0, 60);
  }, [products, q]);
  const toggle = (id) => onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  return (
    <div className="pp-products">
      <div className="pp-products__head">
        <span>{t('prizePool.field.products', { n: selected.length })}</span>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('prizePool.productSearch')} disabled={disabled} aria-label={t('prizePool.productSearch')} />
      </div>
      <ul>
        {shown.map((p) => (
          <li key={p.id}>
            <label>
              <input type="checkbox" checked={selected.includes(p.id)} onChange={() => toggle(p.id)} disabled={disabled} />
              <span>{p.name || p.title}</span>
            </label>
          </li>
        ))}
        {shown.length === 0 && <li className="pp-muted">{t('prizePool.noProducts')}</li>}
      </ul>
    </div>
  );
}

function OfferForm({ brand, initial, reserved, salePercent, onSaved, onCancel, t, lang }) {
  const [form, setForm] = useState(initial);
  const [products, setProducts] = useState([]);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const problem = offerProblem(form);
  const locked = (field) => reserved && !EDITABLE_WHEN_RESERVED.includes(field);
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  useEffect(() => {
    if (form.type === 'cart_coupon') return undefined;
    let alive = true;
    fetchBrandProducts(brand.id).then((list) => alive && setProducts(productListOf(list))).catch(() => {});
    return () => { alive = false; };
  }, [brand.id, form.type]);

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const payload = payloadFromForm(form);
      if (form.id) await updatePrizePoolOffer(brand.id, form.id, form.version, payload);
      else await createPrizePoolOffer(brand.id, payload);
      onSaved();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  const crate = form.type === 'gift_rain';
  return (
    <form className="pp-form" onSubmit={submit} aria-label={form.id ? t('prizePool.editTitle') : t('prizePool.newTitle')}>
      <h3>{form.id ? t('prizePool.editTitle') : t('prizePool.newTitle')}</h3>
      {reserved && <p className="pp-note">{t('prizePool.reservedEditNote')}</p>}
      <div className="pp-grid">
        <label>
          <span>{t('prizePool.field.title')}</span>
          <input value={form.title} maxLength={120} onChange={set('title')} aria-label={t('prizePool.field.title')} />
        </label>
        <label>
          <span>{t('prizePool.field.type')}</span>
          <select value={form.type} onChange={(e) => setForm((f) => ({ ...f, type: e.target.value, discount_type: e.target.value === 'gift_rain' ? '' : f.discount_type || 'percent' }))} disabled={locked('type')} aria-label={t('prizePool.field.type')}>
            {OFFER_TYPES.map((type) => <option key={type} value={type}>{t(`prizePool.type.${type}`)}</option>)}
          </select>
        </label>
        {!crate && (
          <>
            <label>
              <span>{t('prizePool.field.discountType')}</span>
              <select value={form.discount_type} onChange={set('discount_type')} disabled={locked('discount_type')} aria-label={t('prizePool.field.discountType')}>
                {form.type === 'wheel_spin' && <option value="">{t('prizePool.discount.none')}</option>}
                <option value="percent">{t('prizePool.discount.percent')}</option>
                <option value="fixed">{t('prizePool.discount.fixed')}</option>
              </select>
            </label>
            {form.discount_type && (
              <label>
                <span>{form.discount_type === 'percent' ? t('prizePool.field.percent') : t('prizePool.field.amount')}</span>
                <input type="number" min={0} step="any" value={form.discount_value} onChange={set('discount_value')} disabled={locked('discount_value')}
                  aria-label={form.discount_type === 'percent' ? t('prizePool.field.percent') : t('prizePool.field.amount')} />
              </label>
            )}
            {form.discount_type === 'fixed' && (
              <label>
                <span>{t('prizePool.field.currency')}</span>
                <input value={form.currency} maxLength={3} onChange={set('currency')} disabled={locked('currency')} aria-label={t('prizePool.field.currency')} />
              </label>
            )}
            {form.discount_type && (
              <label>
                <span>{t('prizePool.field.minCart')}{form.discount_type === 'fixed' ? ' *' : ''}</span>
                <input type="number" min={0} step="any" value={form.min_cart_value} onChange={set('min_cart_value')} disabled={locked('min_cart_value')} aria-label={t('prizePool.field.minCart')} />
              </label>
            )}
          </>
        )}
        <label>
          <span>{t('prizePool.field.maxUses')}{form.discount_type === 'fixed' ? ' *' : ''}</span>
          <input type="number" min={1} step={1} value={form.max_uses} onChange={set('max_uses')} aria-label={t('prizePool.field.maxUses')} />
        </label>
        <label>
          <span>{t('prizePool.field.monthlyCap')}</span>
          <input type="number" min={1} step={1} value={form.monthly_cap_qty} onChange={set('monthly_cap_qty')} aria-label={t('prizePool.field.monthlyCap')} />
        </label>
        <label>
          <span>{t('prizePool.field.validFrom')}</span>
          <input type="date" value={form.valid_from} onChange={set('valid_from')} disabled={locked('valid_from')} aria-label={t('prizePool.field.validFrom')} />
        </label>
        <label>
          <span>{t('prizePool.field.validUntil')}{form.discount_type === 'fixed' ? ' *' : ''}</span>
          <input type="date" value={form.valid_until} onChange={set('valid_until')} aria-label={t('prizePool.field.validUntil')} />
        </label>
      </div>
      {!crate && form.discount_type && (
        <label className="pp-check">
          <input type="checkbox" checked={form.combinable_with_brand_sales} onChange={set('combinable_with_brand_sales')} disabled={locked('combinable_with_brand_sales')} />
          <span>{t('prizePool.field.combinable')}</span>
        </label>
      )}
      {form.type !== 'cart_coupon' && (
        <ProductPicker products={products} selected={form.product_ids} disabled={locked('product_ids')} t={t}
          onChange={(ids) => setForm((f) => ({ ...f, product_ids: ids }))} />
      )}
      <Preview form={form} salePercent={salePercent} t={t} lang={lang} />
      {problem && <p className="pp-warn">{t(`prizePool.problem.${problem}`)}</p>}
      {error && <div className="panel-card__err" role="alert">{errorText(t, error)}</div>}
      <div className="pp-actions">
        <button type="submit" className="btn-o btn-o--dark pp-btn" disabled={busy || Boolean(problem)}>
          {form.id ? t('prizePool.save') : t('prizePool.create')}
        </button>
        <button type="button" className="pp-btn pp-btn--ghost" onClick={onCancel}>{t('prizePool.cancel')}</button>
      </div>
    </form>
  );
}

function Explainer({ t }) {
  const items = ['day', 'pool', 'contributors', 'noPurchase', 'released', 'partnerDays', 'benefits', 'withdraw', 'combinable'];
  return (
    <details className="pp-explainer">
      <summary><InfoCircle size={15} /> {t('prizePool.howTitle')}</summary>
      <ul>
        {items.map((k) => <li key={k}>{t(`prizePool.how.${k}`)}</li>)}
      </ul>
    </details>
  );
}

/**
 * Prize Pool — what the brand offers for Outfino's community challenges:
 * coupons and gift crates, with a monthly cap, and how each one is used month
 * by month (selected / granted / not completed). Only this brand's numbers are
 * here; other brands' Prize Pool internals are never sent (D14).
 */
export default function PrizePoolTab({ brand, canEdit }) {
  const { t, lang } = useLocale();
  const [month, setMonth] = useState(budapestMonth);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null);
  // The offer waiting for a yes in the withdraw dialog.
  const [confirmWithdraw, setConfirmWithdraw] = useState(null);
  const [revision, setRevision] = useState(0);
  const reload = useCallback(() => setRevision((r) => r + 1), []);

  useEffect(() => {
    let alive = true;
    setError(null);
    fetchPrizePool(brand.id, month).then((d) => alive && setData(d)).catch((e) => alive && setError(e));
    return () => { alive = false; };
  }, [brand.id, month, revision]);

  async function withdraw(offer) {
    try {
      await withdrawPrizePoolOffer(brand.id, offer.id, offer.version);
      reload();
    } catch (e) {
      setError(e);
    }
  }

  const offers = data?.offers ?? [];
  return (
    <div className="panel-card pp">
      <div className="panel-card__row">
        <div>
          <h2 className="panel-card__title">{t('prizePool.title')}</h2>
          <p className="pp-lead">{t('prizePool.lead')}</p>
        </div>
        <div className="pp-month">
          <button type="button" className="cal__nav-btn" onClick={() => setMonth((m) => shiftMonth(m, -1))} aria-label={t('prizePool.prevMonth')}><ChevronLeft size={15} /></button>
          <span aria-live="polite">{month}</span>
          <button type="button" className="cal__nav-btn" onClick={() => setMonth((m) => shiftMonth(m, 1))} aria-label={t('prizePool.nextMonth')}><ChevronRight size={15} /></button>
        </div>
      </div>

      <Explainer t={t} />

      {error && <div className="panel-card__err" role="alert">{errorText(t, error)}</div>}

      {canEdit && !editing && (
        <button type="button" className="btn-o btn-o--dark pp-new" onClick={() => setEditing({ form: { ...EMPTY_OFFER }, reserved: false })}>
          <PlusLg size={14} /> {t('prizePool.new')}
        </button>
      )}
      {editing && (
        <OfferForm brand={brand} initial={editing.form} reserved={editing.reserved} salePercent={data?.brand_max_sale_percent ?? 0} t={t} lang={lang}
          onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); reload(); }} />
      )}

      {data && offers.length === 0 && (
        <div className="pp-empty"><Gift size={22} /> <span>{t('prizePool.empty')}</span></div>
      )}

      <ul className="pp-list">
        {offers.map((o) => {
          const decided = o.stats.decided;
          const reservedEdit = o.display_status === 'reserved';
          return (
            <li key={o.id} className="pp-offer" data-offer-id={o.id}>
              <div className="pp-offer__head">
                <strong>{o.title || t(`prizePool.type.${o.type}`)}</strong>
                <span className="pp-offer__type">{t(`prizePool.type.${o.type}`)}</span>
                {magnitude(o, lang) && <span className="pp-offer__size">{magnitude(o, lang)}</span>}
                {o.combinable_with_brand_sales && <span className="pp-tag">{t('prizePool.combinableTag')}</span>}
                <span className={`pp-chip pp-chip--${o.display_status}`}>{t(`prizePool.status.${o.display_status}`)}</span>
              </div>
              <div className="pp-offer__stats">
                <span>{decided > 0 ? t('prizePool.rate', { decided, granted: o.stats.granted }) : t('prizePool.rateNone')}</span>
                <span>{t('prizePool.selected', { n: o.stats.selected })}</span>
                <span>{t('prizePool.notCompleted', { n: o.stats.released })}</span>
                <span>{t('prizePool.used', { n: o.stats.qty_used })}</span>
                {o.stats.qty_held > 0 && <span>{t('prizePool.held', { n: o.stats.qty_held })}</span>}
                {o.stats.qty_remaining != null
                  ? <span>{t('prizePool.remaining', { n: o.stats.qty_remaining, cap: o.monthly_cap_qty })}</span>
                  : <span>{t('prizePool.noCap')}</span>}
              </div>
              {o.held_days.length > 0 && (
                <div className="pp-offer__held">
                  {t('prizePool.heldDays')}: {o.held_days.map((d) => `${d.date} (${d.reserved_qty} ${t('prizePool.pcs')})`).join(', ')}
                </div>
              )}
              {canEdit && ['offered', 'reserved'].includes(o.display_status) && (
                <div className="pp-offer__actions">
                  <button type="button" className="pp-link" onClick={() => setEditing({ form: formFromOffer(o), reserved: reservedEdit })}>{t('prizePool.edit')}</button>
                  <button type="button" className="pp-link pp-link--danger" onClick={() => setConfirmWithdraw(o)} disabled={!o.can_withdraw.ok}>{t('prizePool.withdraw')}</button>
                  {!o.can_withdraw.ok && o.can_withdraw.reason === 'reserved' && (
                    <span className="pp-muted" data-testid="pp-withdraw-reason">{t('prizePool.withdrawBlocked', { days: o.can_withdraw.days.join(', ') })}</span>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>
      <ConfirmDialog
        open={!!confirmWithdraw}
        title={t('prizePool.withdrawConfirmTitle')}
        body={confirmWithdraw ? t('prizePool.withdrawConfirmBody', { name: confirmWithdraw.title || t(`prizePool.type.${confirmWithdraw.type}`) }) : ''}
        cancelLabel={t('prizePool.withdrawConfirmCancel')}
        confirmLabel={t('prizePool.withdraw')}
        onCancel={() => setConfirmWithdraw(null)}
        onConfirm={() => {
          const offer = confirmWithdraw;
          setConfirmWithdraw(null);
          if (offer) withdraw(offer);
        }}
      />
    </div>
  );
}
